(() => {
  const CFG = window.TSV_CART_CONFIG || {};
  const BASE_THRESHOLD_EUR = Number(CFG.freeShippingThresholdCents || 5000);

  const FX = {
    EUR: 1,
    SEK: 11.5,
    DKK: 7.45,
    PLN: 4.5,
    CZK: 25,
    HUF: 390,
    RON: 5,
    ISK: 150,
    RSD: 117
  };

  function convertToEUR(cents, cart) {
    const currency = cart?.currency || window.Shopify?.currency?.active || 'EUR';
    const rate = FX[currency] || 1;
    return cents / rate;
  }

  function getThresholdInCartCurrency(cart) {
    const currency = cart?.currency || window.Shopify?.currency?.active || 'EUR';
    const rate = FX[currency] || 1;
    return BASE_THRESHOLD_EUR * rate;
  }

  const RECO_MAX = Number(CFG.recos?.max || 3);
  const RECO_VIEW = CFG.recos?.view || 'tsv-recos';

  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  function moneyFormat(cents, cart = null) {
    const currency = cart?.currency || window.Shopify?.currency?.active || 'EUR';
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency
    }).format((cents || 0) / 100);
  }

  function escapeHtml(str) {
    return String(str || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function normalizeImageUrl(url) {
    if (!url) return '';
    let u = String(url).replaceAll('\\/', '/');
    if (u.startsWith('//')) u = 'https:' + u;
    return u;
  }

  function handleFromUrl(url) {
    const m = String(url || '').match(/\/products\/([^?\/]+)/i);
    return m && m[1] ? m[1] : '';
  }

  // Scroll lock — uses overflow + touch-action instead of position:fixed
  // so the page never moves and there's zero flash on close
  let _scrollY = 0;
  function lockScroll(lock) {
    const html = document.documentElement;
    const body = document.body;

    html.classList.toggle('tsv-lock', lock);
    body.classList.toggle('tsv-lock', lock);

    if (lock) {
      _scrollY = window.scrollY || window.pageYOffset || 0;
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      html.style.overscrollBehavior = 'none';
      body.style.overscrollBehavior = 'none';
      html.style.touchAction = 'none';
      body.style.touchAction = 'none';
    } else {
      html.style.overflow = '';
      body.style.overflow = '';
      html.style.overscrollBehavior = '';
      body.style.overscrollBehavior = '';
      html.style.touchAction = '';
      body.style.touchAction = '';
      // No scrollTo needed — content never moved
    }
  }

  // Shopify's /cart.js does NOT expose compare_at_price on line items, even
  // when the variant has one set. To draw "Save €X" pills and the footer
  // total-savings line, we have to fetch each product separately and merge the
  // variant's compare_at_price back onto the cart item. Cached in memory so
  // repeated cart fetches (qty changes, adds) don't refetch the same product.
  const _productCache = {};

  async function enrichWithCompareAt(cart) {
    if (!cart || !Array.isArray(cart.items) || !cart.items.length) return cart;

    const handles = [...new Set(cart.items.map(i => i.handle).filter(Boolean))];
    await Promise.all(handles.map(async handle => {
      if (_productCache[handle]) return;
      try {
        const r = await fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin', cache: 'no-store' });
        if (!r.ok) return;
        _productCache[handle] = await r.json();
      } catch (_) { /* swallow, item just won't get compare_at */ }
    }));

    // Build a variant_id → compare_at_price map across every cached product
    const cmpByVariantId = {};
    Object.values(_productCache).forEach(p => {
      if (!p || !Array.isArray(p.variants)) return;
      p.variants.forEach(v => {
        const cmp = Number(v.compare_at_price || 0);
        const pr = Number(v.price || 0);
        if (cmp > 0 && cmp > pr) cmpByVariantId[v.id] = cmp;
      });
    });

    // Attach compare_at_price (in cents) to each cart item that has one
    cart.items.forEach(it => {
      const cmp = cmpByVariantId[it.variant_id];
      if (cmp) it.compare_at_price = cmp;
    });

    return cart;
  }

  const CartAPI = {
    async getCart() {
      // cache: 'no-store' guarantees we never get a stale cart after a mutation
      const r = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
      if (!r.ok) throw new Error('Failed to load cart');
      const cart = await r.json();
      await enrichWithCompareAt(cart);
      return cart;
    },
    async add(formData) {
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Accept': 'application/json' },
        body: formData
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(json?.description || 'Add to cart failed');
      // /cart/add.js returns the ADDED line item, not the full cart — we still
      // call getCart afterwards in most flows, so no need to enrich here.
      return json;
    },
    async change(lineKey, quantity) {
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity })
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(json?.description || 'Cart update failed');
      // /cart/change.js returns the full cart, so enrich it here too.
      await enrichWithCompareAt(json);
      return json;
    }
  };

  const Store = {
    state: {
      isOpen: false,
      cart: null,
      lastAddedHandle: sessionStorage.getItem('tsv_lastAddedHandle') || '',
      lastAddedVendor: sessionStorage.getItem('tsv_lastAddedVendor') || '',
      recosNotes: [],
      recosScent: [],
      customerPointsToNext: null,
      customerTierMultiplier: 1,
      customerTier: 'Bronze',
      busy: false
    },
    listeners: new Set(),
    set(patch) {
      this.state = { ...this.state, ...patch };
      this.listeners.forEach(fn => fn(this.state));
    },
    subscribe(fn) {
      this.listeners.add(fn);
      return () => this.listeners.delete(fn);
    }
  };

  const root = qs('#tsv-cart');
  if (!root) return;

  function disableDefaultCartUI() {
    try {
      const dn = document.querySelector('cart-notification');
      if (dn && typeof dn.open === 'function') dn.open = () => {};
      if (dn && typeof dn.renderContents === 'function') dn.renderContents = () => {};
    } catch (e) {}
    try {
      const dd = document.querySelector('cart-drawer');
      if (dd && typeof dd.open === 'function') dd.open = () => {};
      if (dd && typeof dd.renderContents === 'function') dd.renderContents = () => {};
    } catch (e) {}
  }
  disableDefaultCartUI();
  // Observe until both default cart elements are found and disabled, then disconnect
  const _cartPatchMo = new MutationObserver(() => {
    disableDefaultCartUI();
    const dn = document.querySelector('cart-notification');
    const dd = document.querySelector('cart-drawer');
    if (dn && dd) _cartPatchMo.disconnect();
  });
  _cartPatchMo.observe(document.documentElement, { childList: true, subtree: true });

  const els = {
    overlay: qs('.tsv-cart__overlay', root),
    overlayClose: qsa('[data-tsv-cart-close]', root),
    panel: qs('.tsv-cart__panel', root),
    body: qs('[data-tsv-cart-body]', root),
    items: qs('[data-tsv-cart-items]', root),
    subtotal: qs('[data-tsv-cart-subtotal]', root),
    shipMsg: qs('[data-tsv-ship-msg]', root),
    shipFill: qs('[data-tsv-ship-fill]', root),
    checkoutCta: qs('.tsv-cart__cta', root),
    cartPoints: qs('[data-tsv-cart-points]', root),
    cartPointsEarned: qs('[data-tsv-cart-points-earned]', root),
    cartTierBadge: qs('[data-tsv-cart-tier-badge]', root),
    cartTierName: qs('[data-tsv-cart-tier-name]', root),
    recoNotesWrap: qs('[data-tsv-reco-notes]', root),
    recoNotesRail: qs('[data-tsv-reco-notes-rail]', root),
    recoScentWrap: qs('[data-tsv-reco-scent]', root),
    recoScentRail: qs('[data-tsv-reco-scent-rail]', root)
  };

  let lastFocus = null;

  function openDrawer({ scrollTop = true } = {}) {
    if (Store.state.isOpen) {
      if (scrollTop && els.body) els.body.scrollTop = 0;
      return;
    }
    lastFocus = document.activeElement;

    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    if (els.overlay) els.overlay.setAttribute('aria-hidden', 'false');
    if (els.overlay) {
      els.overlay.style.opacity = '1';
      els.overlay.style.visibility = 'visible';
      els.overlay.style.pointerEvents = 'auto';
    }

    lockScroll(true);
    Store.set({ isOpen: true });

    if (scrollTop && els.body) els.body.scrollTop = 0;

    const closeBtn = qs('[data-tsv-cart-close]', root);
    closeBtn && closeBtn.focus({ preventScroll: true });
  }

  function closeDrawer() {
    if (!Store.state.isOpen) return;

    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    if (els.overlay) els.overlay.setAttribute('aria-hidden', 'true');
    if (els.overlay) {
      els.overlay.style.opacity = '';
      els.overlay.style.visibility = '';
      els.overlay.style.pointerEvents = '';
    }

    lockScroll(false);
    Store.set({ isOpen: false });

    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  }

  els.overlayClose.forEach(el => el.addEventListener('click', closeDrawer));

  document.addEventListener('click', (e) => {
    if (!Store.state.isOpen) return;
    if (els.panel && els.panel.contains(e.target)) return;
    closeDrawer();
  }, true);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && Store.state.isOpen) closeDrawer();
  });

  root.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab' || !Store.state.isOpen) return;
    const focusables = qsa('button, [href], input, [tabindex]:not([tabindex="-1"])', els.panel)
      .filter(x => !x.disabled && x.offsetParent !== null);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  });

  root.addEventListener('click', (e) => {
    const a = e.target.closest('a[href]');
    if (!a) return;
    if (a.classList.contains('tsv-item__link') || a.classList.contains('tsv-recoItem__link')) closeDrawer();
  });

  function renderProgress(cart) {
    if (!els.shipMsg || !els.shipFill) return;

    const subtotal = Number(cart?.items_subtotal_price || 0);
    const threshold = getThresholdInCartCurrency(cart);

    const remaining = Math.max(0, threshold - subtotal);
    const pct = threshold > 0 ? Math.min(100, (subtotal / threshold) * 100) : 0;

    if (remaining > 0) {
      els.shipMsg.textContent = `${moneyFormat(remaining, cart)} away from free delivery`;
      els.shipFill.style.setProperty('width', `${pct}%`, 'important');
    } else {
      els.shipMsg.textContent = `Free delivery unlocked`;
      els.shipFill.style.setProperty('width', '100%', 'important');
    }
  }

  function animatePointsValue(el, newValue, duration = 400) {
    if (!el) return;

    const currentValue = parseInt(el.textContent, 10) || 0;
    if (currentValue === newValue) return;

    const startTime = Date.now();
    const startValue = currentValue;
    const difference = newValue - startValue;

    const update = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOutQuad = 1 - Math.pow(1 - progress, 2);
      const currentNum = Math.floor(startValue + difference * easeOutQuad);

      el.textContent = currentNum;

      if (progress < 1) {
        requestAnimationFrame(update);
      }
    };

    update();
  }

  const tierColors = {
    'Bronze': '#cd7f32',
    'Silver': '#c0c0c0',
    'Gold': '#ffd700',
    'Platinum': '#e5e4e2'
  };

  function renderCartPoints(cart) {
    if (!els.cartPoints || !els.cartPointsEarned) return;

    const subtotal = Number(cart?.items_subtotal_price || 0);
    const itemCount = Number(cart?.item_count || 0);

    // Gift cards do not earn points — exclude them from the points subtotal.
    const items = Array.isArray(cart?.items) ? cart.items : [];
    let giftCardCents = 0;
    items.forEach(function (it) {
      if (it && it.gift_card) {
        giftCardCents += Number(it.final_line_price != null ? it.final_line_price : (it.line_price || 0));
      }
    });
    const eligibleSubtotal = Math.max(0, subtotal - giftCardCents);

    if (itemCount <= 0 || eligibleSubtotal <= 0) {
      els.cartPoints.hidden = true;
      // CSS sets display:flex !important, so inline must use important to win.
      els.cartPoints.style.setProperty('display', 'none', 'important');
      return;
    }

    els.cartPoints.hidden = false;
    els.cartPoints.style.removeProperty('display');

    const multiplier = Number(Store.state.customerTierMultiplier || 1);
    const earnedPoints = Math.floor((convertToEUR(eligibleSubtotal, cart) / 100) * multiplier);

    animatePointsValue(els.cartPointsEarned, earnedPoints, 400);
  }

  function updateCartBubble(cart) {
    const count = cart?.item_count || 0;
    const bubble = qs('#cart-icon-bubble .cart-count-bubble');
    if (!bubble) return;

    bubble.style.display = count > 0 ? '' : 'none';
    const hidden = bubble.querySelector('span[aria-hidden="true"]');
    const sr = bubble.querySelector('span.visually-hidden');
    if (hidden) hidden.textContent = String(count);
    if (sr) sr.textContent = `${count} items`;
  }

  function renderCart(cart) {
    els.subtotal.textContent = moneyFormat(cart?.items_subtotal_price || 0, cart);
    renderProgress(cart);
    renderCartPoints(cart);

    const isEmpty = !(cart?.items?.length);
    if (els.checkoutCta) {
      if (isEmpty) {
        els.checkoutCta.classList.add('is-disabled');
        els.checkoutCta.setAttribute('aria-disabled', 'true');
        els.checkoutCta.setAttribute('tabindex', '-1');
        els.checkoutCta.setAttribute('href', '#');
      } else {
        els.checkoutCta.classList.remove('is-disabled');
        els.checkoutCta.removeAttribute('aria-disabled');
        els.checkoutCta.removeAttribute('tabindex');
        els.checkoutCta.setAttribute('href', '/checkout');
      }
    }

    if (!cart?.items?.length) {
      els.items.innerHTML = `<div style="padding:18px 0; font-size:14px; font-weight:600; color:#000;">Your bag is empty.</div>`;
      return;
    }

    els.items.innerHTML = cart.items.map(item => {
      const img = item.image ? item.image.replace(/(\.[a-zA-Z]+)$/, '_160x$1') : '';
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      const url = item.url || '/';
      const vendor = item.vendor || '';

      // Split "L Amour EDP 50ml" into title="L Amour" + subtitle="Eau de Parfum · 50ml"
      const split = splitProductTitle(item.product_title);

      // Per-item savings. For compare_at_price sales (your case), Shopify's
      // original_line_price equals final_line_price — the discount only surfaces
      // via item.compare_at_price. We take the max of both signals so this
      // handles compare_at sales AND line-item discount codes.
      const qty = Number(item.quantity || 1);
      const compareAt = Number(item.compare_at_price || 0);
      const finalPrice = Number(item.final_price || item.price || 0);
      const compareAtSavings = compareAt > finalPrice ? (compareAt - finalPrice) * qty : 0;
      const lineDiscountSavings = Number(item.original_line_price || 0) - Number(item.final_line_price || 0);
      const savings = Math.max(compareAtSavings, lineDiscountSavings, 0);
      const saveHtml = savings > 0
        ? `<div class="tsv-item__save">Save ${moneyFormat(savings, cart)}</div>`
        : ``;

      return `
        <div class="tsv-item" data-line-key="${item.key}">
          <a class="tsv-item__link" href="${url}" aria-label="${escapeHtml(item.product_title)}">
            <img class="tsv-item__img" src="${img}" alt="${escapeHtml(item.product_title)}" loading="lazy" width="60" height="60">
          </a>

          <div>
            <a class="tsv-item__link" href="${url}">
              ${vendor ? `<div class="tsv-item__brand">${escapeHtml(vendor)}</div>` : ``}
              <div class="tsv-item__title">${escapeHtml(split.title)}</div>
              ${split.subtitle ? `<div class="tsv-item__subtitle">${escapeHtml(split.subtitle)}</div>` : ``}
            </a>

            ${variant ? `<div class="tsv-item__variant">${escapeHtml(variant)}</div>` : ``}

            <div class="tsv-item__meta">
              <div class="tsv-stepper" role="group" aria-label="Quantity">
                <button type="button" data-qty-dec aria-label="Decrease quantity">−</button>
                <input type="text" value="${item.quantity}" readonly aria-label="Quantity">
                <button type="button" data-qty-inc aria-label="Increase quantity">+</button>
              </div>
              <button class="tsv-item__remove" type="button" data-remove>Remove</button>
            </div>
          </div>

          <div class="tsv-item__priceWrap">
            <div class="tsv-item__price">${moneyFormat(item.final_line_price, cart)}</div>
            ${saveHtml}
          </div>
        </div>
      `;
    }).join('');

    // Footer total-savings line. Compute the same way per item (max of
    // compare_at savings vs line discount savings) so it matches the pills.
    const totalSavings = cart.items.reduce((sum, it) => {
      const q = Number(it.quantity || 1);
      const cmp = Number(it.compare_at_price || 0);
      const fin = Number(it.final_price || it.price || 0);
      const cmpSave = cmp > fin ? (cmp - fin) * q : 0;
      const lineSave = Number(it.original_line_price || 0) - Number(it.final_line_price || 0);
      return sum + Math.max(cmpSave, lineSave, 0);
    }, 0);
    const savingsEl = root.querySelector('[data-tsv-cart-savings]');
    if (savingsEl) {
      const amtEl = savingsEl.querySelector('[data-tsv-cart-savings-amount]');
      if (totalSavings > 0 && amtEl) {
        amtEl.textContent = moneyFormat(totalSavings, cart);
        savingsEl.hidden = false;
      } else {
        savingsEl.hidden = true;
      }
    }
  }

  // Split "L Amour EDP 50ml" → { title: "L Amour", subtitle: "Eau de Parfum · 50ml" }.
  // Mirrors the product page's title splitter (same regex + CONC_MAP).
  function splitProductTitle(raw) {
    const CONC_MAP = {
      'edp':'Eau de Parfum','edt':'Eau de Toilette','edc':'Eau de Cologne','eds':'Eau de Senteur',
      'parfum':'Parfum','elixir':'Elixir','extrait':'Extrait','cologne':'Cologne'
    };
    const CONC_RE = /\b(Eau\s+de\s+Parfum(?:\s+Intense|\s+Fra[iî]che)?|Eau\s+de\s+Toilette(?:\s+Intense)?|Eau\s+de\s+Cologne|Eau\s+de\s+Senteur|Eau\s+Fra[iî]che|Extrait\s+de\s+Parfum|EDP|EDT|EDC|EDS|Parfum|Elixir|Extrait|Cologne)\b(?=(?:\s*(?:limited\s+edition|\(?\s*\d+(?:\.\d+)?\s*(?:[x×]\s*\d+(?:\.\d+)?\s*)?ml\s*\)?|\+\s*\d+(?:\.\d+)?\s*[x×]\s*\d+(?:\.\d+)?\s*ml|(?:gift|discovery|travel)\s+set|duo|set))*\s*$)/i;
    const SIZE_RE = /\(?\s*(\d+(?:\.\d+)?(?:\s*[x×]\s*\d+(?:\.\d+)?)?)\s*ml\b\s*\)?/i;
    /* Product-type suffix — identical list to tsv-search.js so every surface splits alike. */
    const TYPE_RE = /\b(Deodorant\s+Stick|Deodorant\s+Spray|Deodorant|Roll[- ]?On|Bath\s*&\s*Shower\s+Gel|Shower\s+Gel|Shower\s+Cream|Bath\s+Gel|Body\s+Lotion|Body\s+Cream|Body\s+Butter|Body\s+Spray|Body\s+Wash|Body\s+Oil|Hair\s*&\s*Body\s+Mist|Body\s+Mist|Hair\s+Mist|Hand\s+Cream|After\s*shave\s+Balm|After\s*shave\s+Lotion|After\s*shave|Gift\s+Set|Discovery\s+Set|Travel\s+Set|Duo|Beard\s+Oil|Lotion|Soap|Candle)\b/i;
    const titleText = String(raw || '').replace(/\s+/g, ' ').trim();
    if (!titleText) return { title: '', subtitle: '' };

    let name = titleText;
    let parts = [];
    const concMatch = titleText.match(CONC_RE);
    const typeMatch = titleText.match(TYPE_RE);

    if (concMatch) {
      const idx = concMatch.index;
      name = titleText.slice(0, idx).replace(/[\s,·\-–]+$/, '').trim();
      let rest = titleText.slice(idx + concMatch[0].length).trim();
      parts.push(CONC_MAP[concMatch[1].toLowerCase()] || concMatch[1]);

      const sizeMatch = rest.match(SIZE_RE) || titleText.match(SIZE_RE);
      if (sizeMatch) {
        parts.push(sizeMatch[1] + 'ml');
        rest = rest.replace(SIZE_RE, '').trim();
      }
      rest = rest.replace(/\b(EDP|EDT|EDC|EDS|Parfum|Elixir|Extrait|Cologne|Eau\s+de\s+\w+)\b/ig, '');
      rest = rest.replace(/[\s,·\-–]+/g, ' ').trim();
      if (rest) parts.push(rest);

      if (!name) { name = titleText; parts = []; }
    } else if (typeMatch) {
      name = titleText.slice(0, typeMatch.index).replace(/[\s,·\-–]+$/, '').trim();
      parts.push(typeMatch[1]);
      const tsm = titleText.match(SIZE_RE);
      if (tsm) parts.push(tsm[1] + 'ml');
      if (!name) { name = titleText; parts = []; }
    } else {
      const sm = titleText.match(SIZE_RE);
      if (sm) {
        name = titleText.replace(SIZE_RE, '').replace(/[\s,·\-–]+$/, '').trim();
        parts.push(sm[1] + 'ml');
      }
    }

    return { title: name, subtitle: parts.join(' · ') };
  }

  function renderRecoRail(railEl, products) {
    if (!railEl) return;

    railEl.innerHTML = products.map(p => {
      const img = normalizeImageUrl(p.image);
      const title = escapeHtml(p.title);
      const vendor = escapeHtml(p.vendor || p.brand || '');
      const url = p.url || '#';
      const price = moneyFormat(Number(p.price_cents || 0), Store.state.cart);
      const disabled = p.available ? '' : 'disabled';

      return `
        <div class="tsv-recoItem" data-variant-id="${p.variant_id}">
          <a class="tsv-recoItem__link tsv-recoItem__media" href="${url}" aria-label="${title}">
            ${img ? `<img src="${img}" alt="${title}" loading="lazy" width="64" height="72">` : ``}
          </a>

          <div class="tsv-recoItem__main">
            <a class="tsv-recoItem__link" href="${url}">
              ${vendor ? `<div class="tsv-recoItem__brand">${vendor}</div>` : ``}
              <div class="tsv-recoItem__t">${title}</div>
            </a>

            <div class="tsv-recoItem__bottom">
              <div class="tsv-recoItem__p">${price}</div>
              <button class="tsv-recoItem__add" type="button" ${disabled} data-reco-add>Add</button>
            </div>
          </div>
        </div>
      `;
    }).join('');
  }

  function setRecos({ notes = [], scent = [] }) {
    const hasScent = Array.isArray(scent) && scent.length > 0;
    const hasNotes = Array.isArray(notes) && notes.length > 0;

    const showScent = hasScent;
    const showNotes = !hasScent && hasNotes;

    if (els.recoScentWrap) els.recoScentWrap.hidden = !showScent;
    if (els.recoNotesWrap) els.recoNotesWrap.hidden = !showNotes;

    if (showScent) {
      renderRecoRail(els.recoScentRail, scent);
      if (els.recoNotesRail) els.recoNotesRail.innerHTML = '';
    } else if (showNotes) {
      renderRecoRail(els.recoNotesRail, notes);
      if (els.recoScentRail) els.recoScentRail.innerHTML = '';
    } else {
      if (els.recoScentRail) els.recoScentRail.innerHTML = '';
      if (els.recoNotesRail) els.recoNotesRail.innerHTML = '';
    }
  }

  async function loadFallbackByVendor(vendor) {
    if (!vendor) return [];

    const url =
      `/search/suggest.json?q=${encodeURIComponent(vendor)}` +
      `&resources[type]=product&resources[limit]=12` +
      `&resources[options][unavailable_products]=hide`;

    const r = await fetch(url, { credentials: 'same-origin' });
    if (!r.ok) return [];

    const json = await r.json().catch(() => null);
    const results = json?.resources?.results?.products || [];
    if (!results.length) return [];

    const hydrated = await Promise.all(
      results.map(async (p) => {
        try {
          const handle = p.handle;
          if (!handle) return null;

          const pr = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
          if (!pr.ok) return null;

          const pj = await pr.json().catch(() => null);
          const v = (pj?.variants || []).find(x => x && x.available) || (pj?.variants || [])[0] || null;
          if (!v?.id) return null;

          return {
            handle,
            vendor: pj.vendor || p.vendor || '',
            title: pj.title || p.title || '',
            url: (pj.url ? pj.url : (p.url || (`/products/${handle}`))),
            image: (pj.featured_image || p.image || ''),
            price_cents: Number(v.price) || 0,
            available: !!v.available,
            variant_id: v.id
          };
        } catch {
          return null;
        }
      })
    );

    return hydrated.filter(Boolean);
  }

  function syncLastAddedFromCart(cart) {
    const last = cart?.items?.[cart.items.length - 1];
    if (!last) return Store.state.lastAddedHandle || '';

    const vendor = (last && last.vendor) ? String(last.vendor) : '';
    if (vendor && vendor !== Store.state.lastAddedVendor) {
      Store.set({ lastAddedVendor: vendor });
      sessionStorage.setItem('tsv_lastAddedVendor', vendor);
    }

    const url = last.url || '';
    const m = url.match(/\/products\/([^?\/]+)/i);
    const handleFromUrl = m && m[1] ? m[1] : '';
    const handle = handleFromUrl || last.handle || '';

    if (handle && handle !== Store.state.lastAddedHandle) {
      Store.set({ lastAddedHandle: handle });
      sessionStorage.setItem('tsv_lastAddedHandle', handle);
    }

    return handle || Store.state.lastAddedHandle || '';
  }

  // Push a fresh cart into the store + update bubble. Used by sync paths and
  // also directly after a mutation so we don't have to re-fetch /cart.js.
  function applyCart(cart) {
    Store.set({ cart });
    updateCartBubble(cart);
    return cart;
  }

  async function refreshCart() {
    const cart = await CartAPI.getCart();
    return applyCart(cart);
  }

  async function loadRecosForHandle(handle, vendor = '') {
    if (!handle) {
      Store.set({ recosNotes: [], recosScent: [] });
      return;
    }

    try {
      const r = await fetch(`/products/${handle}?view=${encodeURIComponent(RECO_VIEW)}`, { credentials: 'same-origin' });
      const txt = await r.text();

      const start = txt.indexOf('{');
      const end = txt.lastIndexOf('}');
      if (start === -1 || end === -1) {
        const fallback = await loadFallbackByVendor(vendor);
        Store.set({ recosNotes: [], recosScent: fallback.slice(0, RECO_MAX) });
        return;
      }

      const json = JSON.parse(txt.slice(start, end + 1));

      const notesRaw = Array.isArray(json?.notes) ? json.notes : [];
      const scentRaw = Array.isArray(json?.scent) ? json.scent : (Array.isArray(json?.recs) ? json.recs : []);

      const cart = Store.state.cart;
      const inCartHandles = new Set(
        (cart?.items || [])
          .map(i => handleFromUrl(i.url))
          .filter(Boolean)
      );

      const notes = notesRaw
        .filter(p => {
          if (!p || !p.url) return false;
          const h = handleFromUrl(p.url);
          return h ? !inCartHandles.has(h) : true;
        })
        .slice(0, RECO_MAX);

      const scent = scentRaw
        .filter(p => {
          if (!p || !p.url) return false;
          const h = handleFromUrl(p.url);
          return h ? !inCartHandles.has(h) : true;
        })
        .slice(0, RECO_MAX);

      if (notes.length === 0 && scent.length === 0) {
        const fallback = await loadFallbackByVendor(vendor);
        const fallbackFiltered = fallback
          .filter(p => {
            if (!p || !p.url) return false;
            const h = handleFromUrl(p.url);
            return h ? !inCartHandles.has(h) : true;
          })
          .slice(0, RECO_MAX);
        Store.set({ recosNotes: [], recosScent: fallbackFiltered });
        return;
      }

      Store.set({ recosNotes: notes, recosScent: scent });
    } catch {
      const fallback = await loadFallbackByVendor(vendor);
      Store.set({ recosNotes: [], recosScent: fallback.slice(0, RECO_MAX) });
    }
  }

  async function loadCartCustomerPointsStatus() {
    if (!els.cartPoints) return;

    const isLoggedIn = els.cartPoints.getAttribute('data-customer-logged-in') === 'true';
    if (!isLoggedIn) return;

    const rewardsBase = els.cartPoints.getAttribute('data-rewards-base') || '';
    const rewardsProxy = els.cartPoints.getAttribute('data-rewards-proxy') || '/apps/member-vault';

    if (!rewardsBase) return;

    try {
      const tokenRes = await fetch(rewardsProxy, {
        method: 'GET',
        credentials: 'include'
      });

      if (!tokenRes.ok) throw new Error('Token request failed');

      const tokenData = await tokenRes.json();
      if (!tokenData || !tokenData.token) throw new Error('Missing customer token');

      const dashRes = await fetch(rewardsBase + '/api/v1/me/dashboard', {
        method: 'GET',
        headers: {
          'x-customer-token': tokenData.token
        }
      });

      if (!dashRes.ok) throw new Error('Dashboard request failed');

      const dashData = await dashRes.json();

      const tier = dashData?.tier?.name || 'Bronze';
      const multiplier = Number(dashData?.tier?.earnMultiplier || 1);

      Store.set({
        customerPointsToNext: Number(dashData.pointsToNextReward),
        customerTierMultiplier: multiplier,
        customerTier: tier
      });

      // Update tier badge
      if (els.cartTierBadge && els.cartTierName) {
        els.cartTierName.textContent = tier;
        const tierColor = tierColors[tier] || '#cd7f32';
        els.cartTierBadge.style.setProperty('--tier-color', tierColor);
      }

    } catch (err) {
      Store.set({
        customerPointsToNext: null
      });
    }
  }

  async function openAndSync({ scrollTop = true } = {}) {
    openDrawer({ scrollTop });
    const cart = await refreshCart();
    await loadCartCustomerPointsStatus();
    renderCartPoints(cart);
    const handle = syncLastAddedFromCart(cart);
    await loadRecosForHandle(handle, Store.state.lastAddedVendor || '');
  }

  // Called after a known cart mutation. We pass in the cart that the mutation
  // already returned so we don't have to re-fetch /cart.js (avoids stale-cache
  // bugs). Only re-fetch if no cart was passed in.
  async function syncAfterMutation(updatedCart) {
    const y = els.body ? els.body.scrollTop : 0;
    const cart = updatedCart ? applyCart(updatedCart) : await refreshCart();
    if (els.body) els.body.scrollTop = y;

    renderCartPoints(cart);

    const handle = syncLastAddedFromCart(cart) || Store.state.lastAddedHandle || '';
    // Fire-and-forget — UI is already up to date before recos finish loading
    loadRecosForHandle(handle, Store.state.lastAddedVendor || '').catch(() => {});
  }

  // Kept for backwards compat (recos refresh, etc.)
  async function syncWhileOpen() {
    return syncAfterMutation();
  }

  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches(CFG.selectors?.addToCartForm || 'form[action*="/cart/add"]')) return;

    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();

    if (Store.state.busy) return;
    Store.set({ busy: true });

    try {
      const fd = new FormData(form);
      await CartAPI.add(fd);
      await openAndSync({ scrollTop: true });
    } catch (err) {
      /* silent */
    } finally {
      Store.set({ busy: false });
    }
  }, true);

  document.addEventListener('click', (e) => {
    const cartIcon = e.target.closest(CFG.selectors?.cartIcon || '#cart-icon-bubble');
    if (!cartIcon) return;

    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();

    openAndSync({ scrollTop: true });
  }, true);

  root.addEventListener('click', async (e) => {
    const lineEl = e.target.closest('.tsv-item');
    const key = lineEl?.getAttribute('data-line-key');

    if ((e.target.matches('[data-qty-inc]') || e.target.matches('[data-qty-dec]')) && key) {
      const cart = Store.state.cart;
      const item = cart?.items?.find(i => i.key === key);
      if (!item) return;

      const nextQty = e.target.matches('[data-qty-inc]')
        ? item.quantity + 1
        : Math.max(0, item.quantity - 1);

      lineEl.classList.add('is-updating');

      // Optimistic: stepper shows the new qty immediately. If the request
      // fails or quantity hits 0, the re-render will overwrite this anyway.
      const stepperInput = lineEl.querySelector('.tsv-stepper input');
      if (stepperInput) stepperInput.value = String(nextQty);

      try {
        const updatedCart = await CartAPI.change(key, nextQty);
        // Use the cart that /cart/change.js already returned — no second fetch
        await syncAfterMutation(updatedCart);
      } catch (err) {
        /* silent — full re-fetch as recovery */
        try { await refreshCart(); } catch (_) {}
      }
      return;
    }

    if (e.target.matches('[data-remove]') && key) {
      // Optimistic removal: collapse the row instantly so the UI feels snappy
      // regardless of network latency. The full re-render after the change
      // request still runs, and overwrites whatever the user sees.
      lineEl.classList.add('is-removing');
      lineEl.style.pointerEvents = 'none';
      lineEl.style.opacity = '0';
      lineEl.style.transition = 'opacity .15s ease';

      try {
        const updatedCart = await CartAPI.change(key, 0);
        // Use the cart that /cart/change.js already returned — no second fetch.
        // renderCart will rebuild items innerHTML, which discards lineEl.
        await syncAfterMutation(updatedCart);
      } catch (err) {
        // Restore the row if the server rejected the remove
        lineEl.classList.remove('is-removing');
        lineEl.style.pointerEvents = '';
        lineEl.style.opacity = '';
        // Force a recovery render from server state
        try { await refreshCart(); } catch (_) {}
      }
      return;
    }

    const recoBtn = e.target.closest('[data-reco-add]');
    if (recoBtn) {
      const card = e.target.closest('.tsv-recoItem');
      const vid = card?.getAttribute('data-variant-id');
      if (!vid) return;

      recoBtn.disabled = true;

      const fd = new FormData();
      fd.append('id', vid);
      fd.append('quantity', '1');

      try {
        await CartAPI.add(fd);
        await refreshCart();
        // Refresh recos after add
        const handle = syncLastAddedFromCart(Store.state.cart) || Store.state.lastAddedHandle || '';
        loadRecosForHandle(handle, Store.state.lastAddedVendor || '').catch(() => {});
      } catch (err) {
        /* silent */
      } finally {
        recoBtn.disabled = false;
      }
    }
  });

  Store.subscribe((s) => {
    if (s.cart) renderCart(s.cart);
    setRecos({ notes: s.recosNotes, scent: s.recosScent });
  });
})();

// TSV reco info popover
(() => {
  const root = document.getElementById('tsv-cart');
  if (!root) return;

  function closeAll() {
    root.querySelectorAll('.tsv-reco__popover.is-open').forEach(p => {
      p.classList.remove('is-open');
      p.setAttribute('aria-hidden', 'true');
    });
    root.querySelectorAll('[data-tsv-reco-info][aria-expanded="true"]').forEach(b => {
      b.setAttribute('aria-expanded', 'false');
    });
  }

  root.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-tsv-reco-info]');
    const clickedInsidePopover = e.target.closest('.tsv-reco__popover');

    if (!btn && !clickedInsidePopover) {
      closeAll();
      return;
    }

    if (btn) {
      e.preventDefault();
      e.stopPropagation();

      const titleWrap = btn.closest('.tsv-reco__title');
      const pop = titleWrap ? titleWrap.querySelector('.tsv-reco__popover') : null;
      if (!pop) return;

      const isOpen = pop.classList.contains('is-open');
      closeAll();

      if (!isOpen) {
        pop.classList.add('is-open');
        pop.setAttribute('aria-hidden', 'false');
        btn.setAttribute('aria-expanded', 'true');
      }
    }
  }, true);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAll();
  });
})();